import { useCallback, useState } from 'react';

/**
 * The CompanyCode panel next to a conversation (docs/design/mockups/KodPanel.dc.html,
 * KodPanelKapali.dc.html, D-99): shown or hidden and how wide, remembered per
 * user in this browser across reloads and every code conversation. The panel
 * never reopens on its own; only a click that needs it (a file name in an
 * answer, "Önizlemede aç") shows it again.
 */
export const PANEL_MIN = 340;
/** of the conversation's width (the panel and the conversation together) */
export const PANEL_MAX_SHARE = 0.6;
/** an arrow key on the divider moves it this far (Shift: four times) */
export const PANEL_STEP = 24;

export interface CodePanelState {
  hidden: boolean;
  /** pixels; null: the default share of the width */
  width: number | null;
}

const keyOf = (userId: string) => `oppy.codePanel.${userId}`;

export function readCodePanel(userId: string): CodePanelState {
  try {
    const raw = localStorage.getItem(keyOf(userId));
    if (!raw) return { hidden: false, width: null };
    const value = JSON.parse(raw) as Partial<CodePanelState>;
    return { hidden: value.hidden === true, width: typeof value.width === 'number' && value.width >= PANEL_MIN ? Math.round(value.width) : null };
  } catch {
    return { hidden: false, width: null };
  }
}

function writeCodePanel(userId: string, state: CodePanelState): void {
  try {
    localStorage.setItem(keyOf(userId), JSON.stringify(state));
  } catch {
    // private mode: the choice lasts for this page only
  }
}

/** The panel's width kept between 340 px and 60% of the space it shares with the conversation. */
export function clampPanelWidth(width: number, total: number): number {
  return Math.round(Math.max(PANEL_MIN, Math.min(width, Math.max(PANEL_MIN, total * PANEL_MAX_SHARE))));
}

export function useCodePanel(userId: string) {
  const [state, setState] = useState<CodePanelState>(() => readCodePanel(userId));
  const [owner, setOwner] = useState(userId);
  // another account in the same browser has its own choice
  if (owner !== userId) {
    setOwner(userId);
    setState(readCodePanel(userId));
  }
  const update = useCallback(
    (change: Partial<CodePanelState>) => {
      const next = { ...state, ...change };
      writeCodePanel(userId, next);
      setState(next);
    },
    [state, userId],
  );
  return {
    hidden: state.hidden,
    width: state.width,
    setHidden: useCallback((hidden: boolean) => update({ hidden }), [update]),
    setWidth: useCallback((width: number) => update({ width }), [update]),
  };
}
